import { CSSProperties, useCallback, useMemo, useState } from 'react'
import { Node as SlateNode, Transforms } from 'slate'
import { ReactEditor, RenderElementProps, useSlate } from 'slate-react'

import {
  insertLink,
  removeTypeWithPath,
  updateImageLink,
} from 'core-components/library/richTextEditor/helpers/slateHelper'
import { TImageElement } from 'core-components/library/richTextEditor/interface/custom-types'
import {
  LinkButtonColors,
  TextButtonFontSize,
  TLink,
} from 'core-components/library/richTextEditor/interface/Tlink'
import { LinkSettingsDialog } from 'core-components/library/richTextEditor/toolbar/components/link/LinkSettingsDialog'

import { LinkView } from './LinkView'

interface IProps {
  style: CSSProperties
}

export const Link = (props: RenderElementProps & IProps) => {
  const [linkDialogVisible, setLinkDialogVisible] = useState(false)
  const isImageLink = props.element['view'] === 'image'

  const editor = useSlate()

  const link: TLink = useMemo(() => {
    return {
      text: SlateNode.string(props.element),
      url: props.element['url'],
      backgroundColor: props.element['backgroundColor'],
      openInNewTab: props.element['openInNewTab'],
      fontSize: props.element['fontSize'],
      iconPosition: props.element['iconPosition'],
      icon: props.element['icon'],
    }
  }, [props.element])

  const removeLink = useCallback(() => {
    const path = ReactEditor.findPath(editor, props.element)
    removeTypeWithPath(editor, path)
  }, [editor, props.element])

  const onCloseLinkDialog = useCallback(() => {
    setLinkDialogVisible(false)
  }, [])

  const className = useMemo(() => {
    if (
      props.element['view'] === 'button' &&
      (props.element['backgroundColor'] || props.element['fontSize'])
    ) {
      const backgroundValue = LinkButtonColors.find(
        (item) => item.name === props.element['backgroundColor']
      )
      const fontSizeValue = TextButtonFontSize.find(
        (item) => item.name === props.element['fontSize']
      )
      return props.element['className'] + ' ' + backgroundValue?.value + ' ' + fontSizeValue?.value
    }
    return props.element['className']
  }, [props.element['view'], props.element['backgroundColor'], props.element['fontSize']])

  const imageLinkSrc = useMemo(() => {
    if (isImageLink) {
      const urls = (props.element.children as TImageElement[]).map((item) => item.url)
      return urls[0]
    }

    return ''
  }, [props.element.children, isImageLink])

  return (
    <>
      <LinkSettingsDialog
        onCancel={onCloseLinkDialog}
        onSave={(newLink) => {
          Transforms.select(editor, ReactEditor.findPath(editor, props.element))
          if (isImageLink) {
            updateImageLink(editor, newLink, ReactEditor.findPath(editor, props.element))
          } else {
            insertLink(editor, newLink, props.element['view'])
          }
          setLinkDialogVisible(false)
        }}
        link={link}
        src={imageLinkSrc}
        onClose={() => setLinkDialogVisible(false)}
        show={linkDialogVisible}
        type={props.element['view']}
      />
      <LinkView
        link={link}
        onOpenLink={() => setLinkDialogVisible(true)}
        removeLink={removeLink}
        style={props.style}
        type={props.element['view']}
        element={props.element}
        attributes={props.attributes}
        className={className}
      >
        {props.children}
      </LinkView>
    </>
  )
}
